/* eslint-disable react/prop-types */
import { Box, Grid, Divider, Button, Tooltip, Avatar } from "@mui/material";
import React, { useContext, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import * as Sentry from "@sentry/react";

// Icons Import
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIcon from "@mui/icons-material/PhoneInTalkOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutlineOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import GroupAddIcon from "@mui/icons-material/GroupAddOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";

import Typography from "../../../../components/common/new/Typography/Typography";
import { SET_SELECTED_MEMBER_PROFILE } from "../../../../redux/actionTypes/dashboard";
import { ROUTES } from "../../../../utils/constants";
import styles from "./styles";
import { AlertStatus, PERMISSIONS } from "../../../../contants";
import { getInitials } from "../utlis";
import ProfilePictureModal from "../../../../components/common/new/ProfilePictureModal/ProfilePictureModal";
import { ToastAlertContext } from "../../../../config/providers/ToastAlert";
import Member from "../../../../services/api/member";
import Family from "../../../../services/api/Family";
import Loader from "../../../../components/common/Loader/Loader";

// --- Helper Components ---

function HeaderRowItem({ icon, text, isLink = false }) {
  if (!text) return null;
  return (
    <Box sx={styles.headerRowItem}>
      {icon}
      <Typography
        style={{
          ...styles.headerRowText,
          color: isLink ? "#111827" : "#111827",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function GridDetailItem({ icon, label, value }) {
  return (
    <Box sx={styles.gridDetailContainer}>
      <Box sx={styles.gridIconWrapper}>{icon}</Box>
      <Box sx={styles.gridTextWrapper}>
        <Typography style={styles.gridLabel}>{label}</Typography>
        <Typography style={styles.gridValue}>{value || "-"}</Typography>
      </Box>
    </Box>
  );
}

export default function MemberDetailBoxs({
  memberInfo,
  hasPermission,
  onRefresh,
}) {
  const {
    memberId,
    fullName,
    mobileNumber,
    emailId,
    nationalityName,
    addressLine1,
    cityName,
    pincode,
    stateName,
    introducerName,
    introducerEmail,
    introducerMobileNumber,
    divisionName,
    subDivisionName,
    dateOfBirth,
    dateOfJoining,
    universityName,
    collegeName,
    professionName,
  } = memberInfo;
  // Array me se "MAINLINE_POSITION" dhoondhne ka logic
  const mainlinePosition = memberInfo?.positions?.find(
    (pos) => pos.positionType === "MAINLINE_POSITION"
  );

  // Agar MAINLINE_POSITION mile toh uska naam dikhao, warna pehle wale ka dikha do (fallback)
  const positionName =
    mainlinePosition?.name || memberInfo?.positions?.[0]?.name || "";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  // Family Data State
  const [familyData, setFamilyData] = useState([]);
  const [isFamilyLoading, setIsFamilyLoading] = useState(false);

  const toast = useContext(ToastAlertContext);
  const { id } = useParams();
  const [familyId, setFamilyId] = useState(null);

  // Fetch Family Data
  useEffect(() => {
    const getFamilyData = async () => {
      if (!memberId) return;
      try {
        setIsFamilyLoading(true);
        const response = await Family.getFamilyDetailsByMemberId(memberId, {
          page: 0,
          size: 10,
        });
        setFamilyId(response.data?.familyId); // ✅ yaha set karo

        const list =
          response.data?.familyMembers ||
          response.data?.content ||
          response.data ||
          [];

        // ✅ FIX: Filter out the current member from the family list
        const filteredList = list.filter(
          (member) => String(member.memberId) !== String(memberId)
        );

        setFamilyData(filteredList);
      } catch (error) {
        console.error("Family API Error:", error);
      } finally {
        setIsFamilyLoading(false);
      }
    };

    getFamilyData();
  }, [memberId]);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen((prev) => !prev);
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setSelectedImage(file);
  };

  const handleImageUpload = async () => {
    try {
      if (selectedImage) {
        const file = new File([selectedImage], "image.jpg", {
          type: "image/jpeg",
        });
        const formData = new FormData();
        formData.append("file", file);

        setIsPhotoLoading(true);
        await Member.uploadProfilePhoto({
          memberId: Number(id),
          data: formData,
        });
        toast.show("Image Uploaded Successfully", AlertStatus.SUCCESS);
        setTimeout(() => {
          onRefresh();
          setModalOpen(false);
        }, 2000);
      }
      setIsPhotoLoading(false);
    } catch (error) {
      toast.show(error.message, AlertStatus.ERROR);
      Sentry.captureException(error);
    }
  };

  const onEditClick = () => {
    setIsPhotoLoading(true);
    setTimeout(() => {
      dispatch({ type: SET_SELECTED_MEMBER_PROFILE, payload: memberInfo });
      navigate(`${ROUTES.MEMBER_EDIT_PROFILE.path}/${memberId}`);
      setIsPhotoLoading(false);
    }, 2000);
  };

  // ✅ UPDATE: Debugging add kiya hai check karne ke liye
  const handleFamilyClick = (famId) => {
    console.log("Clicked Family ID:", famId); // Console me check karna ID print hui ya nahi

    if (famId) {
      navigate(`/family/view/${famId}/preview`);
    } else {
      console.warn("Error: Family ID nahi mila data mein!");
    }
  };

  const handleAddFamily = () => {
    navigate(`/members/family/create`);
  };

  const canEdit =
    hasPermission(PERMISSIONS.UPDATE_MEMBER) ||
    hasPermission(PERMISSIONS.UPDATE_MEMBER_DOB) ||
    hasPermission(PERMISSIONS.UPDATE_MEMBER_NAME);

  // ✅ FIX: Preparing Family Content separately to avoid nested ternary error
  let familyContent;
  if (isFamilyLoading) {
    familyContent = (
      <Typography style={{ fontSize: "12px", color: "#6B7280" }}>
        Loading...
      </Typography>
    );
  } else if (familyData && familyData.length > 0) {
    familyContent = (
      <Box display="flex" alignItems="center">
        {familyData.slice(0, 4).map((member, index) => {
          const tooltipText = member.relationType
            ? `${member.fullName} (${member.relationType})`
            : member.fullName;

          return (
            <Tooltip
              // ✅ FIX: Using unique memberId instead of index
              key={member.memberId}
              title={tooltipText}
              arrow
            >
              <Avatar
                src={member.profilePhotoUrl}
                alt={member.fullName}
                sx={{
                  ...styles.familyAvatar,
                  zIndex: 10 - index,
                  marginLeft: index === 0 ? 0 : "-10px",
                  cursor: "pointer",
                  border: "2px solid #fff",
                }}
                onClick={() => handleFamilyClick(familyId)}
              >
                {getInitials(member.fullName)}
              </Avatar>
            </Tooltip>
          );
        })}
        {familyData.length > 4 && (
          <Box sx={styles.moreCount}>+{familyData.length - 4}</Box>
        )}
      </Box>
    );
  } else {
    familyContent = (
      <Button
        startIcon={<GroupAddIcon />}
        onClick={handleAddFamily}
        sx={styles.addFamilyBtn}
      >
        Add
      </Button>
    );
  }

  return (
    <Box sx={styles.mainCard}>
      {/* Top Banner */}
      <Box sx={styles.headerBanner}>
        <Box sx={styles.actionButtonsContainer}>
          {canEdit && (
            <Button sx={styles.actionBtn} onClick={onEditClick}>
              Edit
            </Button>
          )}
        </Box>
        <Box sx={styles.headerLocationRow}>
          <Typography style={styles.headerLocationText}>
            {[
              memberInfo?.areaName,
              memberInfo?.zoneName,
              memberInfo?.regionName,
              memberInfo?.chapterName,
              memberInfo?.districtName,
              memberInfo?.blockName,
            ]
              .filter(Boolean)
              .join(" > ")}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.contentWrapper}>
        <Box sx={styles.topSection}>
          <Box sx={styles.photoSection}>
            <button
              type="button"
              onClick={handleModalOpen}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={styles.profileImageContainer}
            >
              {memberInfo.profilePhotoUrl ? (
                <img
                  src={memberInfo.profilePhotoUrl}
                  alt="Profile"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box sx={styles.initialsBox}>{getInitials(fullName)}</Box>
              )}
              {hasPermission(PERMISSIONS.UPDATE_MEMBER_PROFILE_PHOTO) && (
                <Box sx={{ ...styles.editOverlay, opacity: isHovered ? 1 : 0 }}>
                  Update
                </Box>
              )}
            </button>

            <Box sx={styles.memberIdContainer}>
              <BadgeIcon sx={styles.iconSmall} />
              <Typography style={styles.memberIdText}>
                Member ID: {memberId}
              </Typography>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mt: "50px",
              mx: { xs: 0, md: 0 },
              display: { xs: "none", md: "block" },
            }}
          />

          <Box sx={styles.infoSection}>
            <Box sx={styles.nameRow}>
              <Typography style={styles.userName}>{fullName}</Typography>
            </Box>

            <Box sx={styles.metaRow}>
              <HeaderRowItem
                icon={<EmailIcon sx={styles.iconSmall} />}
                text={emailId}
                isLink
              />
              <HeaderRowItem
                icon={<PhoneIcon sx={styles.iconSmall} />}
                text={mobileNumber}
              />
              <HeaderRowItem
                icon={<WorkspacePremiumOutlinedIcon sx={styles.iconSmall} />}
                text={[positionName, divisionName].filter(Boolean).join(", ")}
              />
            </Box>

            <Box mt={1} display="flex" alignItems="flex-start" gap="6px">
              <LocationOnIcon sx={{ ...styles.iconSmall, mt: 0.2 }} />
              <Typography style={styles.addressText}>
                {addressLine1}, {cityName}, {stateName}
                {pincode ? ` - ${pincode}` : ""}, {nationalityName}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3, borderColor: "#E5E7EB" }} />

        {/* --- Bottom Section: Details Grid --- */}
        <Box sx={styles.bottomSection}>
          <Grid container spacing={3} rowSpacing={4}>
            {/* ROW 1 */}
            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<CalendarTodayIcon sx={styles.gridIcon} />}
                label="Member Since"
                value={
                  dateOfJoining
                    ? moment(dateOfJoining).format("MMMM, YYYY")
                    : "-"
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<PersonOutlineIcon sx={styles.gridIcon} />}
                label="Date of Birth"
                value={
                  dateOfBirth ? moment(dateOfBirth).format("MMMM, YYYY") : "-"
                }
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<WorkOutlineIcon sx={styles.gridIcon} />}
                label="Profession"
                value={professionName}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<BusinessOutlinedIcon sx={styles.gridIcon} />}
                label="Division / Sub Division"
                value={
                  [divisionName, subDivisionName].filter(Boolean).join(" / ") ||
                  "-"
                }
              />
            </Grid>

            {/* ROW 2 - Family Members First */}
            {/* ROW 2 - Family Members First */}
            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<FamilyRestroomOutlinedIcon sx={styles.gridIcon} />}
                label="Family Members"
                value={familyContent} // ✅ Seedha variable pass kar diya
              />
            </Grid>
            <Grid item xs={6} sm={4} md={4.8}>
              <Tooltip
                title={`Contact: ${introducerMobileNumber || "-"} | Email: ${
                  introducerEmail || "-"
                }`}
              >
                <div>
                  <GridDetailItem
                    icon={<ContactPhoneOutlinedIcon sx={styles.gridIcon} />}
                    label="Introduced By / Mob. / Email"
                    value={
                      [
                        introducerName,
                        introducerMobileNumber,
                        introducerEmail,
                      ].join(" / ") || "-"
                    }
                  />
                </div>
              </Tooltip>
            </Grid>

            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<SchoolOutlinedIcon sx={styles.gridIcon} />}
                label="University"
                value={universityName}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<SchoolOutlinedIcon sx={styles.gridIcon} />}
                label="College"
                value={collegeName}
              />
            </Grid>

            <Grid item xs={6} sm={4} md={2.4}>
              <GridDetailItem
                icon={<LocationOnIcon sx={styles.gridIcon} />}
                label="Nationality"
                value={nationalityName}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Modals */}
      <ProfilePictureModal
        open={modalOpen}
        onClose={handleModalClose}
        handleImageChange={handleImageChange}
        handleImageUpload={handleImageUpload}
        memberProfile={memberInfo}
        isPhotoLoading={isPhotoLoading}
        toast={toast}
      />
      {isPhotoLoading && <Loader />}
    </Box>
  );
}
